import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Loader2, MailCheck } from 'lucide-react';
import TurnstileWidget from '@/components/TurnstileWidget';

export default function Signup() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ email: '', password: '', full_name: '', is_load_pilot: false, pilot_class: 'Class 1', pilot_number: '', company: '' });
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [sentTo, setSentTo] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    if (form.is_load_pilot && !form.pilot_number.trim()) return toast.error('Please enter your pilot number');
    if (!turnstileToken) return toast.error('Please complete the human-verification check');
    setLoading(true);
    try {
      await register({ ...form, turnstile_token: turnstileToken });
      setSentTo(form.email);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  if (sentTo) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center" data-testid="signup-verify-sent">
          <div className="w-16 h-16 rounded-2xl bg-teal-100 flex items-center justify-center mx-auto mb-6">
            <MailCheck className="w-8 h-8 text-teal-600" />
          </div>
          <h1 className="font-display font-black text-3xl mb-3">Check your email</h1>
          <p className="text-zinc-600 mb-2">We've sent a verification link to</p>
          <p className="font-semibold text-zinc-900 mb-6">{sentTo}</p>
          <p className="text-sm text-zinc-500 mb-8">Click the link in that email to activate your account. You'll be able to log in once your email is verified. The link is valid for 24 hours.</p>
          <Link to="/login" className="btn btn-primary w-full" data-testid="signup-goto-login-after">Go to login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 bg-neutral-950 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1647456751346-44ad13e4b5cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxOZXclMjBaZWFsYW5kJTIwaGlnaHdheSUyMHN1bnNldHxlbnwwfHx8fDE3ODMyMzUxNzN8MA&ixlib=rb-4.1.0&q=85')" }}
        />
        <div className="relative m-16 self-end text-white max-w-md">
          <div className="text-xs tracking-[0.2em] uppercase text-teal-400 mb-2">Join PTT NZ Alerts</div>
          <h2 className="font-display font-black text-4xl leading-tight">Free forever for in-portal + email alerts.</h2>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <img src="/brand/logo-icon@2x.png" alt="PTT" className="w-10 h-10 rounded-lg" />
            <span className="font-display font-black text-xl">PTT NZ Alerts</span>
          </Link>
          <h1 className="font-display font-black text-3xl mb-2">Create your account</h1>
          <p className="text-zinc-600 mb-8">Start receiving NZ highway alerts in under 30 seconds.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
            <div>
              <label className="label">Full name</label>
              <input className="input" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="signup-name" />
            </div>
            <div>
              <label className="label">Email</label>
              <input className="input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email" />
            </div>
            <div>
              <label className="label">Password</label>
              <input className="input" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="signup-password" />
            </div>
            <label className="flex items-start gap-3 p-3.5 rounded-xl border-2 border-zinc-200 cursor-pointer hover:border-teal-300 transition" data-testid="signup-load-pilot-row">
              <input type="checkbox" checked={form.is_load_pilot} onChange={(e) => setForm({ ...form, is_load_pilot: e.target.checked })} className="mt-0.5 w-5 h-5 accent-teal-500" data-testid="signup-load-pilot" />
              <span className="text-sm">
                <span className="font-semibold text-zinc-900">I'm a Load Pilot</span>
                <span className="block text-zinc-500 text-xs mt-0.5">Free. Schedule oversize/overweight loads, auto-build corridors, and coordinate head-on meets.</span>
              </span>
            </label>
            {form.is_load_pilot && (
              <div className="space-y-4 p-4 rounded-xl bg-teal-50/50 border border-teal-100" data-testid="signup-pilot-fields">
                <div>
                  <label className="label">Pilot class</label>
                  <select className="input" value={form.pilot_class} onChange={(e) => setForm({ ...form, pilot_class: e.target.value })} data-testid="signup-pilot-class">
                    <option value="Class 1">Class 1 Pilot</option>
                    <option value="Class 2">Class 2 Pilot</option>
                  </select>
                </div>
                <div>
                  <label className="label">Pilot number</label>
                  <input className="input" value={form.pilot_number} onChange={(e) => setForm({ ...form, pilot_number: e.target.value })} placeholder="Your NZTA pilot certificate number" data-testid="signup-pilot-number" />
                </div>
                <div>
                  <label className="label">Company <span className="text-zinc-400 font-normal">(optional)</span></label>
                  <input className="input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Your pilot/transport company" data-testid="signup-company" />
                </div>
              </div>
            )}
            <TurnstileWidget onVerify={setTurnstileToken} className="pt-1" />
            <button type="submit" className="btn btn-primary w-full" disabled={loading || !turnstileToken} data-testid="signup-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create account'}
            </button>
          </form>

          <div className="mt-8 text-sm text-zinc-600 text-center">
            Already have one? <Link to="/login" className="text-orange-600 font-semibold hover:underline" data-testid="signup-goto-login">Log in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
